import {
  hasDotBlock,
  headerCarriesLegend,
  labelHazardCharacteristics,
  labelHeaderFor,
} from "encamp-shared/src/waste/hazardousWasteLabelVisuals";
import {
  LabelContentFieldsFragment,
  WasteAccumulationAreaType,
  WasteKind,
} from "generated-graphql/graphql";
import { QR_CODE_SERVICE_URL } from "util/qrCodeService";

type DotProfileContent = Pick<
  LabelContentFieldsFragment,
  "dotProperShippingName" | "dotIdNumber" | "dotHazardClass"
>;

/**
 * Whether a container's DOT shipping profile is entirely unset, and so whether
 * the DOT block renders. The inverse of the shared `hasDotBlock`, which the
 * printed label gates the same block on -- shared rather than reimplemented so
 * the preview cannot show a block the PDF omits.
 */
export function isDotProfileIncomplete(content: DotProfileContent): boolean {
  return !hasDotBlock(content);
}

/**
 * Whether the label is about to print with no indication of the hazards of its
 * contents.
 *
 * 40 CFR 262.16(b)(6)(i)(B) and 262.17(a)(5)(i)(B) require one alongside the
 * marking and the accumulation date. The label satisfies it with the DOT
 * hazard-class diamond, or failing that with the characteristics its federal
 * waste codes name -- so this is only true when neither is available, which
 * leaves the drum with a required mark missing.
 *
 * Scoped to labels carrying the hazardous-waste marking, since that is where
 * the requirement applies: used oil (279.22(c)(1)) and universal waste
 * (273.14) need only their own marking.
 */
export function isHazardIndicationMissing(
  content: Pick<
    LabelContentFieldsFragment,
    | "wasteKind"
    | "classification"
    | "universalWasteCategory"
    | "dotProperShippingName"
    | "dotIdNumber"
    | "dotHazardClass"
    | "federalWasteCodes"
  >
): boolean {
  const header = labelHeaderFor(
    content.wasteKind,
    content.classification,
    content.universalWasteCategory
  );
  if (!headerCarriesLegend(header)) return false;
  return !hasDotBlock(content) && labelHazardCharacteristics(content).length === 0;
}

/**
 * Whether a central-accumulation-area label is about to print with no
 * accumulation start date, which 40 CFR 262.16(b)(6)(i)(C) and
 * 262.17(a)(5)(i)(C) require to be visible on the container.
 *
 * A satellite area is exempt -- it is volume-limited rather than time-limited,
 * and only needs a date once the excess starts accumulating
 * (262.15(a)(6)(iii)) -- so an undated satellite label is correct and must not
 * warn.
 */
export function isAccumulationStartDateMissing(
  content: Pick<LabelContentFieldsFragment, "accumulationStartDate" | "accumulationAreaType">
): boolean {
  return (
    content.accumulationAreaType === WasteAccumulationAreaType.Central &&
    content.accumulationStartDate == null
  );
}

/**
 * Whether a universal-waste container is about to print a marking that does
 * not name its waste type.
 *
 * 40 CFR 273.14 requires the marking to identify the TYPE of universal waste
 * ("Universal Waste - Lamp(s)"); a bare "Universal Waste" is not one of the
 * authorized phrases, so a label without the type goes on the drum
 * non-compliant. The category is derived onto the stream from its waste lines,
 * so it is absent while a container accumulates ahead of its first shipment,
 * and absent when the stream's lines disagree about which universal waste it
 * holds.
 */
export function isUniversalWasteTypeMissing(
  content: Pick<LabelContentFieldsFragment, "wasteKind" | "universalWasteCategory">
): boolean {
  return content.wasteKind === WasteKind.UniversalWaste && content.universalWasteCategory == null;
}

/**
 * Whether the marking is about to be chosen with nothing to choose from.
 *
 * A stream always carries a classification, so this is the container with no
 * stream assigned (`WasteContainer.wasteStreamId` is nullable).
 * `labelHeaderFor` prints the conservative "HAZARDOUS WASTE" there rather than
 * asserting "NON-HAZARDOUS WASTE" from missing data -- safe to handle, but not
 * a fact about this drum, so say so instead of letting it read as one.
 *
 * Only the kinds that leave the marking to classification count: used oil,
 * universal waste and pharmaceuticals name themselves whatever the
 * classification is.
 */
export function isClassificationMissing(
  content: Pick<LabelContentFieldsFragment, "wasteKind" | "classification">
): boolean {
  const kindDecidesMarking = content.wasteKind != null && content.wasteKind !== WasteKind.Rcra;
  return content.classification == null && !kindDecidesMarking;
}

/**
 * Builds the qr-code-generator service URL for a container's label QR,
 * copying the param set QRCodeDisplay builds (components/QRCodeDisplay/index.tsx)
 * minus its screen/print theme colors, which don't apply to a printed label.
 */
export function qrImageUrl(qrUrl: string): string {
  const params = new URLSearchParams({
    text: qrUrl,
    width: "400",
    margin: "2",
    errorCorrectionLevel: "H",
  });
  return `${QR_CODE_SERVICE_URL}?${params.toString()}`;
}
